export interface ReturnPurchasePaymentOption {
  id: number;
  name: string;
  company_id?: number | null;
  is_default?: boolean | number;
  currency_code?: string | null;
}

interface HttpResponse {
  data: any;
}

interface HttpClient {
  get: (url: string, config?: Record<string, any>) => Promise<HttpResponse>;
}

export const normalizeReturnPurchaseScopeId = (
  value: unknown,
): number | null => {
  const source =
    value && typeof value === "object" && "id" in value
      ? (value as { id?: unknown }).id
      : value;
  const parsed = Number(source);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const extractReturnPurchasePaymentModes = (
  payload: any,
): ReturnPurchasePaymentOption[] => {
  let values: any[] = [];
  if (Array.isArray(payload)) values = payload;
  else if (Array.isArray(payload?.paymentModes)) values = payload.paymentModes;
  else if (Array.isArray(payload?.payment_modes)) values = payload.payment_modes;
  else if (Array.isArray(payload?.data)) values = payload.data;

  return values
    .filter(
      (mode: any) =>
        mode &&
        normalizeReturnPurchaseScopeId(mode.id) &&
        mode.active !== false &&
        mode.is_active !== false &&
        Number(mode.is_active ?? 1) !== 0,
    )
    .map((mode: any) => ({
      id: Number(mode.id),
      name: String(mode.name || mode.mode_name || "Mode de paiement"),
      company_id:
        normalizeReturnPurchaseScopeId(mode.company_id) ??
        (mode.company_id == null ? null : undefined),
    }))
    .sort(
      (
        left: ReturnPurchasePaymentOption,
        right: ReturnPurchasePaymentOption,
      ) => {
        if (left.company_id && !right.company_id) return -1;
        if (!left.company_id && right.company_id) return 1;
        return left.name.localeCompare(right.name, "fr");
      },
    );
};

export const extractReturnPurchaseTreasuryAccounts = (
  payload: any,
): ReturnPurchasePaymentOption[] => {
  const values = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return values
    .filter(
      (account: any) =>
        account && normalizeReturnPurchaseScopeId(account.id),
    )
    .map((account: any) => ({
      id: Number(account.id),
      name: String(account.name || "Compte de trésorerie"),
      is_default: account.is_default,
      currency_code: account.currency_code || null,
    }));
};

export const loadReturnPurchasePaymentOptions = async (
  client: HttpClient,
  companyId: unknown,
  warehouseId: unknown,
): Promise<{
  paymentModes: ReturnPurchasePaymentOption[];
  treasuryAccounts: ReturnPurchasePaymentOption[];
}> => {
  const normalizedCompanyId = normalizeReturnPurchaseScopeId(companyId);
  const normalizedWarehouseId = normalizeReturnPurchaseScopeId(warehouseId);

  if (!normalizedCompanyId || !normalizedWarehouseId) {
    throw new Error(
      "Entreprise ou établissement introuvable pour charger les options de paiement.",
    );
  }

  const [paymentModesResponse, treasuryAccountsResponse] = await Promise.all([
    client.get("/api/payment-modes", {
      params: { company_id: normalizedCompanyId, limit: 200 },
    }),
    client.get("/api/treasury/accounts", {
      params: {
        company_id: normalizedCompanyId,
        warehouse_id: normalizedWarehouseId,
        include_inactive: false,
        module_scope: "commercial",
      },
    }),
  ]);

  return {
    paymentModes: extractReturnPurchasePaymentModes(paymentModesResponse.data),
    treasuryAccounts: extractReturnPurchaseTreasuryAccounts(
      treasuryAccountsResponse.data,
    ),
  };
};
